/**
 * Pantalla 2 del §7: workspace de la entidad.
 *
 * Tres zonas: cabecera con la ficha de la entidad, pestañas con las distintas
 * vistas del mismo grafo, y panel lateral de detalle que se abre al pinchar
 * cualquier stakeholder desde cualquier pestaña.
 *
 * La pestaña activa vive en la URL para que un enlace sea compartible y el
 * botón «atrás» del navegador funcione como se espera.
 */

import type { RowSelectionState } from '@tanstack/react-table'
import {
  ArrowLeft,
  Download,
  FileText,
  GitMerge,
  History,
  Link2,
  Network,
  Table2,
  Target,
} from 'lucide-react'
import { Suspense, lazy, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { api } from '../api/cliente'
import { useEntidad } from '../api/hooks'
import { useDescartarStakeholder, useProfundizarStakeholder } from '../api/hooks'
import type { Corrida, FiltrosTabla } from '../api/tipos'
import { mensajeDeError, useAvisos } from '../ui/avisos-contexto'
import { AccionesFicha } from '../componentes/curaduria/AccionesFicha'
import { AccionesMasivas } from '../componentes/curaduria/AccionesMasivas'
import { PanelDuplicados } from '../componentes/curaduria/PanelDuplicados'
import { PanelDetalle } from '../componentes/PanelDetalle'
import { ProgresoCorrida } from '../componentes/ProgresoCorrida'
import { TablaStakeholders } from '../componentes/TablaStakeholders'
import { VistaCorridas, VistaFuentes, VistaRelaciones, VistaTemas } from '../componentes/Vistas'
import { Boton, Cargando, Chip, Contador, Error as EstadoError, Vacio } from '../ui/primitivos'
import { cx } from '../ui/util'

// Cytoscape pesa unos 400 kB y solo lo necesita una de las siete pestañas:
// se carga la primera vez que se abre el grafo, no al entrar al workspace.
const Grafo = lazy(() =>
  import('../componentes/grafo/Grafo').then((m) => ({ default: m.Grafo })),
)

const PESTANAS = [
  { id: 'grafo', etiqueta: 'Grafo', icono: Network },
  { id: 'tabla', etiqueta: 'Tabla', icono: Table2 },
  { id: 'relaciones', etiqueta: 'Relaciones', icono: Link2 },
  { id: 'temas', etiqueta: 'Materialidad', icono: Target },
  { id: 'fuentes', etiqueta: 'Fuentes', icono: FileText },
  { id: 'duplicados', etiqueta: 'Duplicados', icono: GitMerge },
  { id: 'corridas', etiqueta: 'Corridas', icono: History },
] as const

type IdPestana = (typeof PESTANAS)[number]['id']

function CabeceraEntidad({ entidadId }: { entidadId: number }) {
  const { data: entidad } = useEntidad(entidadId)
  if (!entidad) return null

  const contexto = [entidad.sector, entidad.region, entidad.pais].filter(Boolean)
  const cifras = entidad.situacion_actual?.cifras_clave ?? []

  return (
    <div className="border-b border-[var(--color-borde)] bg-[var(--color-panel)] px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="text-[var(--color-texto-tenue)] hover:text-[var(--color-texto)]"
              aria-label="Volver al inicio"
            >
              <ArrowLeft size={15} />
            </Link>
            <h1 className="truncate text-[16px] font-semibold tracking-tight">
              {entidad.nombre}
            </h1>
          </div>
          {contexto.length > 0 && (
            <p className="mt-0.5 ml-6 text-[12px] text-[var(--color-texto-suave)]">
              {contexto.join(' · ')}
              {entidad.grupo_controlador && ` · grupo ${entidad.grupo_controlador}`}
            </p>
          )}
          {entidad.alias.length > 0 && (
            <div className="mt-1.5 ml-6 flex flex-wrap gap-1">
              {entidad.alias.slice(0, 4).map((a) => (
                <Chip key={a}>{a}</Chip>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {(['json', 'csv', 'xlsx'] as const).map((formato) => (
            <a
              key={formato}
              href={api.urlDescarga(`/entities/${entidadId}/export/?format=${formato}`)}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--color-borde-fuerte)] px-2 py-1 text-[12px] hover:bg-[var(--color-acento-suave)]"
              download
            >
              <Download size={12} />
              {formato.toUpperCase()}
            </a>
          ))}
        </div>
      </div>

      {entidad.situacion_actual?.estado && (
        <p className="mt-2 ml-6 max-w-3xl text-[12px] text-[var(--color-texto-suave)]">
          {entidad.situacion_actual.estado}
        </p>
      )}

      {cifras.length > 0 && (
        <dl className="mt-2 ml-6 flex flex-wrap gap-x-6 gap-y-1">
          {cifras.map((c) => (
            <div key={c.etiqueta}>
              <dt className="text-[10px] tracking-wide text-[var(--color-texto-tenue)] uppercase">
                {c.etiqueta}
              </dt>
              <dd className="tabular text-[13px] font-medium">
                {c.valor}
                {c.unidad && (
                  <span className="ml-1 text-[11px] font-normal text-[var(--color-texto-suave)]">
                    {c.unidad}
                  </span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}

export function Workspace() {
  const { id, pestana } = useParams()
  const navegar = useNavigate()
  const entidadId = Number(id)

  const consulta = useEntidad(Number.isFinite(entidadId) ? entidadId : undefined)
  const [detalle, setDetalle] = useState<number | null>(null)
  const [filtros, setFiltros] = useState<FiltrosTabla>({ page: 1 })
  const [seleccionados, setSeleccionados] = useState<RowSelectionState>({})
  // Corrida lanzada desde la curaduría, para poder seguir su progreso sin
  // salir del workspace.
  const [corridaEnCurso, setCorridaEnCurso] = useState<Corrida | null>(null)

  const avisos = useAvisos()
  const profundizarNodo = useProfundizarStakeholder(entidadId)
  const descartarNodo = useDescartarStakeholder(entidadId)

  // Acciones que el grafo dispara desde su menú contextual y el doble clic.
  const alProfundizarNodo = (id: number) =>
    profundizarNodo.mutate(
      { id },
      {
        onSuccess: (corrida) => {
          if (corrida.reutilizada) {
            avisos.info('Ya había una profundización reciente sobre ese actor; se reutilizó.')
          } else {
            avisos.exito('Profundización lanzada', {
              detalle: 'Puede tardar varios minutos. El resultado se integra al grafo solo.',
            })
            setCorridaEnCurso(corrida)
          }
        },
        onError: (error) => avisos.error('No se pudo profundizar', mensajeDeError(error)),
      },
    )

  const alDescartarNodo = (id: number) =>
    descartarNodo.mutate(
      { id, motivo: 'Descartado desde el grafo' },
      {
        onSuccess: (ficha) =>
          avisos.exito(`«${ficha.nombre}» descartado`, {
            detalle: 'Desapareció del grafo. Nada se borró.',
          }),
        onError: (error) => avisos.error('No se pudo descartar', mensajeDeError(error)),
      },
    )

  const activa: IdPestana = (PESTANAS.find((p) => p.id === pestana)?.id ?? 'tabla') as IdPestana

  if (!Number.isFinite(entidadId)) {
    return <Vacio titulo="Entidad no válida" detalle="La dirección no apunta a una entidad." />
  }
  if (consulta.isLoading) return <Cargando texto="Cargando la entidad…" />
  if (consulta.isError) {
    return (
      <div className="p-4">
        <EstadoError error={consulta.error} reintentar={() => consulta.refetch()} />
        <Boton className="mt-3" onClick={() => navegar('/')}>
          Volver al inicio
        </Boton>
      </div>
    )
  }

  const entidad = consulta.data!
  const nSeleccionados = Object.values(seleccionados).filter(Boolean).length

  return (
    <div className="flex h-[calc(100vh-3rem)] flex-col">
      <CabeceraEntidad entidadId={entidadId} />

      <nav
        className="flex shrink-0 gap-0.5 overflow-x-auto border-b border-[var(--color-borde)] bg-[var(--color-panel)] px-2"
        role="tablist"
      >
        {PESTANAS.map(({ id: idPestana, etiqueta, icono: Icono }) => {
          const seleccionada = activa === idPestana
          return (
            <button
              key={idPestana}
              role="tab"
              aria-selected={seleccionada}
              onClick={() => navegar(`/entidad/${entidadId}/${idPestana}`)}
              className={cx(
                'inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-[13px] whitespace-nowrap transition-colors',
                seleccionada
                  ? 'border-[var(--color-acento)] font-medium text-[var(--color-texto)]'
                  : 'border-transparent text-[var(--color-texto-suave)] hover:text-[var(--color-texto)]',
              )}
            >
              <Icono size={14} />
              {etiqueta}
              {idPestana === 'tabla' && <Contador valor={entidad.n_stakeholders} />}
              {idPestana === 'duplicados' && (
                <Contador valor={entidad.n_duplicados_pendientes} destacado />
              )}
            </button>
          )
        })}
      </nav>

      {nSeleccionados > 0 && activa === 'tabla' && (
        <AccionesMasivas
          entidadId={entidadId}
          ids={Object.keys(seleccionados)
            .filter((id) => seleccionados[id])
            .map(Number)}
          onLimpiar={() => setSeleccionados({})}
        />
      )}

      {corridaEnCurso && (
        <div className="shrink-0 border-b border-[var(--color-borde)] p-2">
          <ProgresoCorrida
            corridaId={corridaEnCurso.id}
            compacto
            alCancelar={() => setCorridaEnCurso(null)}
          />
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-hidden bg-[var(--color-panel)]">
          {activa === 'grafo' && (
            <Suspense fallback={<Cargando texto="Cargando el motor del grafo…" />}>
              <Grafo
                entidadId={entidadId}
                seleccionado={detalle}
                onSeleccionar={setDetalle}
                onProfundizar={alProfundizarNodo}
                onDescartar={alDescartarNodo}
                onCorridaLanzada={setCorridaEnCurso}
              />
            </Suspense>
          )}

          {activa === 'tabla' && (
            <TablaStakeholders
              entidadId={entidadId}
              filtros={filtros}
              onFiltros={setFiltros}
              onAbrir={setDetalle}
              seleccionados={seleccionados}
              onSeleccion={setSeleccionados}
            />
          )}

          {activa === 'relaciones' && (
            <div className="scroll-sutil h-full overflow-auto">
              <VistaRelaciones entidadId={entidadId} onAbrir={setDetalle} />
            </div>
          )}

          {activa === 'temas' && (
            <div className="scroll-sutil h-full overflow-auto">
              <VistaTemas
                entidadId={entidadId}
                onAbrir={setDetalle}
                onCorridaLanzada={setCorridaEnCurso}
              />
            </div>
          )}

          {activa === 'fuentes' && <VistaFuentes entidadId={entidadId} />}

          {activa === 'duplicados' && (
            <div className="scroll-sutil h-full overflow-auto">
              <PanelDuplicados entidadId={entidadId} onAbrir={setDetalle} />
            </div>
          )}

          {activa === 'corridas' && <VistaCorridas entidadId={entidadId} />}
        </div>

        <PanelDetalle
          stakeholderId={detalle}
          onCerrar={() => setDetalle(null)}
          acciones={(ficha) => (
            <AccionesFicha ficha={ficha} onCorridaLanzada={setCorridaEnCurso} />
          )}
        />
      </div>
    </div>
  )
}
